import { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { toast } from 'sonner';

/**
 * Custom hook for managing chat sessions
 * Handles creating, loading, switching, updating, and deleting sessions
 */
const useChatSessions = (documentId, sheetId) => {
  // Per-sheet history: the list URL is scoped by sheet so switching sheets shows
  // only that sheet's sessions. ponytail: build it once from the current sheetId.
  const sessionsUrl = () =>
    `/ai/chat/sessions/${documentId}` + (sheetId ? `?sheet_id=${sheetId}` : '');
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);            // archived conversations (read-only)
  const [viewingArchivedId, setViewingArchivedId] = useState(null);

  // Load sessions when the document OR the active sheet changes, so each sheet
  // shows its own history. Reset the view first to avoid flashing the prior
  // sheet's messages while the new list loads.
  useEffect(() => {
    // Stale-response guard: switching sheets fast (A→B→A) races two loads; the
    // late one must NOT repaint the sheet you already left. `cancelled` flips on
    // cleanup so a superseded load skips every setState.
    let cancelled = false;
    const loadSessions = async () => {
      if (!documentId) return;

      setLoadingSessions(true);
      setCurrentSessionId(null);
      setMessages([]);
      try {
        const response = await axiosInstance.get(sessionsUrl());
        if (cancelled) return;

        if (response.data && response.data.length > 0) {
          setSessions(response.data);
          // Load the most recent session by default (inlined so the guard also
          // covers the messages fetch — loadSessionMessages has no guard).
          const mostRecent = response.data[0];
          setCurrentSessionId(mostRecent.id);
          const msgs = await axiosInstance.get(`/ai/chat/sessions/${mostRecent.id}/messages`);
          if (cancelled) return;
          if (msgs.data) setMessages(msgs.data);
        } else {
          setSessions([]);
        }
      } catch (error) {
        if (!cancelled) console.error('Error loading sessions:', error);
      } finally {
        if (!cancelled) setLoadingSessions(false);
      }
    };

    loadSessions();
    return () => { cancelled = true; };
  }, [documentId, sheetId]);

  // Refresh only the sessions list (T-022): used when a quick-command action
  // creates/uses a session outside the sidebar so it appears without clobbering
  // the current messages view.
  const refreshSessions = async () => {
    if (!documentId) return;
    try {
      const response = await axiosInstance.get(sessionsUrl());
      if (response.data) setSessions(response.data);
    } catch (error) {
      console.error('Error refreshing sessions:', error);
    }
  };

  // Load messages for a specific session
  const loadSessionMessages = async (sessionId) => {
    try {
      const response = await axiosInstance.get(
        `/ai/chat/sessions/${sessionId}/messages`
      );

      if (response.data) {
        setMessages(response.data);
      }
    } catch (error) {
      console.error('Error loading session messages:', error);
      toast.error('Error al cargar los mensajes');
    }
  };

  // Create a new session
  const createNewSession = async () => {
    try {
      const response = await axiosInstance.post('/ai/chat/sessions', {
        document_id: documentId,
        sheet_id: sheetId || null,
        title: `Chat ${new Date().toLocaleString()}`
      });

      const newSession = response.data;
      setSessions([newSession, ...sessions]);
      setCurrentSessionId(newSession.id);
      setMessages([]);
      return newSession.id;
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Error al crear la sesión');
      return null;
    }
  };

  // Switch to a different session
  const switchSession = async (sessionId) => {
    setCurrentSessionId(sessionId);
    await loadSessionMessages(sessionId);
    toast.success('Sesión cargada');
  };

  // Start editing session title
  const startEditingSession = (sessionId, currentTitle, event) => {
    event.stopPropagation();
    setEditingSessionId(sessionId);
    setEditingTitle(currentTitle);
  };

  // Save edited session title
  const saveSessionTitle = async (sessionId, event) => {
    if (event) event.stopPropagation();

    if (!editingTitle.trim()) {
      toast.error('El título no puede estar vacío');
      return;
    }

    try {
      await axiosInstance.patch(`/ai/chat/sessions/${sessionId}`, {
        title: editingTitle.trim()
      });

      setSessions(sessions.map(s =>
        s.id === sessionId ? { ...s, title: editingTitle.trim() } : s
      ));

      setEditingSessionId(null);
      setEditingTitle('');
      toast.success('Título actualizado');
    } catch (error) {
      console.error('Error updating session title:', error);
      toast.error('Error al actualizar el título');
    }
  };

  // Cancel editing
  const cancelEditingSession = (event) => {
    if (event) event.stopPropagation();
    setEditingSessionId(null);
    setEditingTitle('');
  };

  // "Limpiar chat": archive the current conversation into the immutable history
  // and start the (same) chat clean. Never deletes; a fresh session is created
  // lazily on the next message send (currentSessionId=null triggers auto-create).
  const clearChat = async () => {
    const sid = currentSessionId;
    if (sid) {
      try {
        await axiosInstance.post(`/ai/chat/sessions/${sid}/clear`);
      } catch (error) {
        console.error('Error clearing chat:', error);
        toast.error('No se pudo limpiar el chat');
        return;
      }
    }
    setViewingArchivedId(null);
    setCurrentSessionId(null);
    setMessages([]);
    refreshSessions();
    setHistory([]); // force the history panel to re-fetch on next open
    toast.success('Chat limpiado (guardado en el historial)');
  };

  // Load the archived history (read-only) for this sheet/document.
  const historyUrl = () =>
    `/ai/chat/sessions/${documentId}?archived=true` + (sheetId ? `&sheet_id=${sheetId}` : '');
  const loadHistory = async () => {
    if (!documentId) return;
    try {
      const r = await axiosInstance.get(historyUrl());
      setHistory(r.data || []);
    } catch (error) {
      console.error('Error loading history:', error);
      setHistory([]);
    }
  };

  // View an archived conversation read-only, and return to the active chat.
  const viewArchived = async (sessionId) => {
    setViewingArchivedId(sessionId);
    await loadSessionMessages(sessionId);
  };
  const exitArchived = async () => {
    setViewingArchivedId(null);
    try {
      const r = await axiosInstance.get(sessionsUrl());
      const list = r.data || [];
      setSessions(list);
      if (list.length > 0) {
        setCurrentSessionId(list[0].id);
        await loadSessionMessages(list[0].id);
      } else {
        setCurrentSessionId(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error returning to chat:', error);
    }
  };

  // Continuar una conversación archivada: desarchiva en el backend, sale del
  // modo solo-lectura y la convierte en la sesión activa para seguir escribiendo.
  const resumeSession = async (sessionId) => {
    try {
      await axiosInstance.post(`/ai/chat/sessions/${sessionId}/resume`);
    } catch (error) {
      console.error('Error resuming session:', error);
      toast.error('No se pudo continuar la conversación');
      return;
    }
    setViewingArchivedId(null);
    await switchSession(sessionId);
    refreshSessions();
    setHistory([]); // force the history panel to re-fetch on next open
  };

  // Soft-delete a conversation from the user's view. NOT a physical delete: the
  // server retains it (deslinde legal). Confirmed via the dialog.
  const confirmDeleteSession = (sessionId, event) => {
    if (event) event.stopPropagation();
    setSessionToDelete(sessionId);
  };
  const deleteSession = async () => {
    if (!sessionToDelete) return;
    try {
      await axiosInstance.delete(`/ai/chat/sessions/${sessionToDelete}`);
      setHistory((prev) => prev.filter((s) => s.id !== sessionToDelete));
      setSessions((prev) => prev.filter((s) => s.id !== sessionToDelete));
      if (viewingArchivedId === sessionToDelete) await exitArchived();
      toast.success('Conversación eliminada de tu vista');
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('No se pudo eliminar');
    } finally {
      setSessionToDelete(null);
    }
  };

  // Discard a session BY ID (no sessionToDelete state): used by the "Nueva
  // conversación" prompt to delete the current conversation before starting a
  // fresh one. If it is the active session, the chat is left clean so the next
  // send auto-creates a new session (same lazy pattern as clearChat).
  const discardSession = async (sessionId) => {
    if (!sessionId) return;
    try {
      await axiosInstance.delete(`/ai/chat/sessions/${sessionId}`);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
        setMessages([]);
      }
      refreshSessions();
      toast.success('Conversación eliminada');
    } catch (error) {
      console.error('Error discarding session:', error);
      toast.error('No se pudo eliminar');
    }
  };

  return {
    sessions,
    currentSessionId,
    setCurrentSessionId,
    loadingSessions,
    sessionToDelete,
    setSessionToDelete,
    editingSessionId,
    editingTitle,
    setEditingTitle,
    messages,
    setMessages,
    createNewSession,
    switchSession,
    startEditingSession,
    saveSessionTitle,
    cancelEditingSession,
    confirmDeleteSession,
    deleteSession,
    discardSession,
    refreshSessions,
    // Limpiar + historial (read-only) + soft-delete.
    clearChat,
    history,
    loadHistory,
    viewingArchivedId,
    viewArchived,
    exitArchived,
    resumeSession,
    // T-153: expuesto para que el polling de jobs async pueda refrescar las
    // burbujas del asistente a medida que el backend completa rondas.
    loadSessionMessages,
  };
};

export default useChatSessions;
